import axios from '@fs/zion-axios'
import { appName } from '@fs/zion-config'
import { getFeatureFlag, flagsAreReady } from '@fs/zion-flags'

let cachedContactPromises = {}

const contactDataUrlWithCisId = (cisId) => `/service/tree/ftuser/contacts/${cisId}`
const contactDataUrlWithCpUserId = (cpUserId) => `/service/tree/ftuser/contacts/?contributorId=${cpUserId}`

const missingIdErrorMessage = new Error(`Missing id in fetchContact. Either a cisId or cpUserId is expected`)

/**
 * Handles contact promise resolution and caching
 * @param {Object} params - The parameters object
 * @param {Promise} params.contactPromise - Promise that resolves to contact data
 * @param {string} [params.cisId] - The CIS ID for caching
 * @param {string} [params.cpUserId] - The CP User ID for caching
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Re-throws any error from the contact promise
 */
async function returnContact({ contactPromise, cisId, cpUserId }) {
  cachedContactPromises[cisId || cpUserId] = contactPromise
  try {
    const contact = (await contactPromise).data
    contactPromise[contact.cpUserId] = contact
    contactPromise[contact.id] = contact
    return contact
  } catch (error) {
    cachedContactPromises[cisId || cpUserId] = null
    throw error
  }
}

/**
 * Maps contact data from CIS Admin format to FT User format
 * @param {Object} cisAdminContact - Contact data from CIS Admin service
 * @returns {Object} Contact data in FT User format
 */
function mapContactToFtUserShape(cisAdminContact) {
  return {
    id: cisAdminContact.id,
    contactName: cisAdminContact.contactName,
    email: cisAdminContact.email,
    alternateEmail: null,
    phoneNumber: cisAdminContact?.sms?.number,
    mailingAddress: null,
    preferredLanguage: cisAdminContact.preferredLanguage,
    aliases: [],
    givenName: cisAdminContact.givenName,
    familyName: cisAdminContact.familyName,
    country: cisAdminContact.country,
    displayName: cisAdminContact.displayName,
  }
}

/**
 * Fetches contact data from CIS Admin service
 * @param {string} cisId - The CIS ID to fetch contact for
 * @returns {Promise<{data: Object}>} Contact data wrapped in data property
 * @throws {Error} Throws error if API request fails
 */
async function getContactCisAdminService(cisId) {
  const url = `/service/ident/cis/cis-admin-api/v1/user/${cisId}`
  const response = await axios.get(url, {
    headers: { Accept: 'application/familysearch-identity-result+v4.2+json' },
  })

  const contact = response.data.users[0]
  // wrapping the contact behind `data` so the API of returnContact won't need to change
  return { data: mapContactToFtUserShape(contact) }
}

/**
 * Fetches contact data by CIS ID
 * @param {string} cisId - The CIS ID to fetch contact for
 * @param {boolean} [forceNewEndpoint] - Whether to force using the new CIS Admin endpoint
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Throws error if cisId is missing or API request fails
 */
/* eslint-disable-next-line no-unused-vars -- just keeping for now while evaluating the platform API */
export async function fetchContactByCisId(cisId, forceNewEndpoint) {
  if (!cisId) throw missingIdErrorMessage
  await flagsAreReady()
  const noFtUser = getFeatureFlag('frontier_zion_ftUserReplacement', { appName })
  // this is very useful for manual testing in storybook
  // if (forceNewEndpoint || noFtUser.isOn) {
  //   clearCacheForTest()
  if (noFtUser.isOn) {
    return returnContact({
      contactPromise: cachedContactPromises[cisId] || getContactCisAdminService(cisId),
      cisId,
    })
  }

  return returnContact({
    contactPromise: cachedContactPromises[cisId] || axios.get(contactDataUrlWithCisId(cisId)),
    cisId,
  })
}

/**
 * Fetches contact data by CP User ID
 * @param {string} cpUserId - The CP User ID to fetch contact for
 * @param {boolean} [forceNewEndpoint] - Whether to force using the new CIS Admin endpoint
 * @returns {Promise<Object>} The contact data with cpUserId property added
 * @throws {Error} Throws error if cpUserId is missing or API request fails
 */
/* eslint-disable-next-line no-unused-vars -- just keeping for now while evaluating the platform API */
export async function fetchContactByCpUserId(cpUserId, forceNewEndpoint) {
  if (!cpUserId) throw missingIdErrorMessage
  await flagsAreReady()
  const noFtUser = getFeatureFlag('frontier_zion_ftUserReplacement', { appName })

  // this is very useful for manual testing in storybook
  // if (forceNewEndpoint || noFtUser.isOn) {
  // clearCacheForTest()
  if (noFtUser.isOn) {
    // in the non-ftuser world, we first have to map from cpUserId to cisId, and then make the call to cis-admin with the cisId
    const cisId = await getCisIdFromCpUserId(cpUserId)
    return {
      ...(await returnContact({
        contactPromise: cachedContactPromises[cisId] || getContactCisAdminService(cisId),
        cpUserId,
      })),
      cpUserId,
    }
  }
  return returnContact({
    contactPromise: cachedContactPromises[cpUserId] || axios.get(contactDataUrlWithCpUserId(cpUserId)),
    cpUserId,
  })
}

/**
 * Maps CP User ID to CIS ID by querying the tf tree service
 * @param {string} cpUserId - The CP User ID to map
 * @returns {Promise<string>} The corresponding CIS ID
 * @throws {Error} Throws error if API request fails
 */
async function getCisIdFromCpUserId(cpUserId) {
  const res = await axios.get(`/service/tree/tf/user/${cpUserId}/persons?nameFilter=nopersonsplease&pageSize=5`)
  return res.data.contributorCisId
}

/**
 * Generic function to fetch contact data by either CIS ID or CP User ID
 * @param {Object} params - The parameters object
 * @param {string} [params.cisId] - The CIS ID to fetch contact for
 * @param {string} [params.cpUserId] - The CP User ID to fetch contact for
 * @param {boolean} [params.forceNewEndpoint=false] - Whether to force using the new CIS Admin endpoint
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Throws error if neither cisId nor cpUserId is provided, or if API request fails
 */
export async function fetchContact({ cisId, cpUserId, forceNewEndpoint = false }) {
  if (cisId) {
    return fetchContactByCisId(cisId, forceNewEndpoint)
  }
  if (cpUserId) {
    return fetchContactByCpUserId(cpUserId, forceNewEndpoint)
  }
  throw missingIdErrorMessage
}

/**
 * Clears the cached contact promises - used for testing purposes
 * @returns {void}
 */
export function clearCacheForTest() {
  cachedContactPromises = {}
}
